import { motion } from "framer-motion";
import {
  Brain,
  Clock,
  Compass,
  HeartHandshake,
  HeartPulse,
  Mail,
  MessageCircle,
  MoonStar,
  Sparkles,
  Users,
} from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import { Asterisk, EASE, Reveal } from "@/components/site/Reveal";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

const SKILLS = [
  {
    icon: MessageCircle,
    title: "Conversation & Communication",
    items: ["Starting and maintaining conversations", "Listening and responding", "Following conversations", "Asking and answering questions", "Expressing wants and needs"],
  },
  {
    icon: Brain,
    title: "Social Understanding",
    items: ["Reading social cues", "Recognizing emotions", "Perspective taking", "Flexible thinking", "Social problem-solving"],
  },
  {
    icon: HeartHandshake,
    title: "Friendship & Connection",
    items: ["Making and maintaining friendships", "Personal space", "Cooperative activities", "Managing disagreements"],
    featured: true,
  },
  {
    icon: Compass,
    title: "Independence & Community",
    items: ["Self-advocacy", "Workplace and community participation", "Handling changes", "Daily living skills"],
  },
  {
    icon: HeartPulse,
    title: "Emotional & Behavioral Skills",
    items: ["Coping with frustration", "Self-regulation", "Waiting", "Accepting \u201cno\u201d", "Appropriate responses to challenging situations"],
  },
];

const APPROACH = [
  { icon: Users, title: "Small Groups", text: "Limited group sizes so every participant gets meaningful attention and practice." },
  { icon: HeartHandshake, title: "Meaningful Connections", text: "Groups formed with age, communication level, and social goals in mind." },
  { icon: Sparkles, title: "Real-Life Practice", text: "Conversations, role-playing, group activities, and real-world problem-solving." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function AdultSocialSkills() {
  usePageMeta(
    "Evening Social Skills Program for Adults (18+) | Milestone Pediatric Therapy",
    "An evening social skills ABA program for adults 18+ — small groups, real-life practice, and meaningful connection. Evenings 6:00–7:30 PM. Contact us for rates and enrollment."
  );

  return (
    <div data-testid="adult-program-page" className="min-h-screen bg-background">
      <Nav />
      <main>
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
          <div className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-brand-pale/80 blur-3xl" aria-hidden="true" />
          <div className="absolute top-1/2 -left-48 h-80 w-80 rounded-full bg-brand-yellow/30 blur-3xl" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-brand-lightblue/60 bg-white/70 backdrop-blur px-5 py-2 mb-8"
              data-testid="adult-eyebrow"
            >
              <MoonStar className="h-3.5 w-3.5 text-brand-coral" />
              <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-brand-blue">
                Evening Program — Adults 18+
              </span>
            </motion.div>

            <h1 className="font-serif font-medium tracking-tight text-brand-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl" data-testid="adult-heading">
              <MaskedLine delay={0.15}>Evening Social Skills</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-brand-coral">for Adults</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
              className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-brand-muted"
              data-testid="adult-subcopy"
            >
              Growth doesn't stop at 18. Our evening ABA program helps adults build social
              confidence, communication, and meaningful connection — in a supportive small-group
              setting.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
              data-testid="adult-quick-facts"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <Clock className="h-4 w-4 text-brand-coral" /> Evenings, 6:00 PM – 7:30 PM
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <Users className="h-4 w-4 text-brand-coral" /> Small Groups • Ages 18+
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
              className="mt-8"
            >
              <a
                href={`mailto:${EMAIL}?subject=Adult%20Evening%20Social%20Skills%20Program`}
                data-testid="adult-cta-enroll"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-[#f06666]"
              >
                Ask About Enrollment
                <Mail className="h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white/60 border-y border-brand-lightblue/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <Asterisk className="h-4 w-4 text-brand-coral" />
                <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                  Skills We Target
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue mb-14" data-testid="adult-skills-heading">
                Confidence for <em className="text-brand-coral">work, community, and life.</em>
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="adult-skills-grid">
              {SKILLS.map((s, i) => (
                <Reveal key={s.title} delay={0.07 * i}>
                  <article
                    data-testid={`adult-skill-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group h-full rounded-[2rem] border p-8 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift ${
                      s.featured
                        ? "bg-brand-blue text-white border-brand-blue shadow-soft"
                        : "bg-white border-brand-lightblue/40 shadow-soft hover:border-brand-coral/40"
                    }`}
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-6 transition-transform duration-500 group-hover:scale-110 ${
                        s.featured ? "bg-white/15 text-brand-yellow" : "bg-brand-pale text-brand-blue"
                      }`}
                    >
                      <s.icon className="h-6 w-6" />
                    </span>
                    <h3 className={`font-serif text-2xl font-semibold mb-5 ${s.featured ? "text-white" : "text-brand-blue"}`}>
                      {s.title}
                    </h3>
                    <ul className="space-y-2.5">
                      {s.items.map((item) => (
                        <li key={item} className={`flex items-start gap-2.5 text-sm md:text-base ${s.featured ? "text-white/85" : "text-brand-muted"}`}>
                          <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${s.featured ? "bg-brand-yellow" : "bg-brand-coral"}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid md:grid-cols-3 gap-8" data-testid="adult-approach-grid">
              {APPROACH.map((a, i) => (
                <Reveal key={a.title} delay={0.08 * i}>
                  <div className="text-center px-4">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-pale text-brand-blue mb-5">
                      <a.icon className="h-7 w-7" />
                    </span>
                    <h3 className="font-serif text-2xl font-semibold text-brand-blue mb-3">{a.title}</h3>
                    <p className="text-sm md:text-base leading-relaxed text-brand-muted">{a.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="rounded-[2.5rem] bg-brand-blue text-white shadow-lift p-10 md:p-16 text-center" data-testid="adult-enroll-cta">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-5">
                  Evenings, <em className="text-brand-yellow">6:00 – 7:30 PM</em>
                </h2>
                <p className="max-w-2xl mx-auto text-white/85 text-base md:text-lg leading-relaxed mb-9">
                  Contact Milestone Pediatric Therapy for current rates, enrollment, and
                  availability.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <a
                    href={`mailto:${EMAIL}?subject=Adult%20Evening%20Social%20Skills%20Program`}
                    data-testid="adult-enroll-email-button"
                    className="group inline-flex items-center gap-2 rounded-full bg-white text-brand-blue font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift"
                  >
                    <Mail className="h-4 w-4" />
                    {EMAIL}
                  </a>
                  <a
                    href="tel:+18325919535"
                    data-testid="adult-enroll-phone-button"
                    className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold px-8 py-4 transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-white/10"
                  >
                    (832) 591-9535
                  </a>
                </div>
                <p className="mt-8 text-xs text-white/60 max-w-xl mx-auto" data-testid="adult-disclaimer">
                  Participation is based on individual needs and appropriateness for the group. ABA
                  services are provided with appropriate clinical oversight and in accordance with
                  applicable professional requirements.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
